'use client';

import { useCallback, useOptimistic, useTransition } from 'react';
import { usePathname, useRouter, getPathname } from '@/src/i18n/navigation';
import { useSearchParams, type ReadonlyURLSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';

type Props = {
  justUpdateUrl?: boolean;
};

// عرّفنا الـ reducer برة كـ function مستقلة، بأنواعها واضحة
const searchParamsReducer = (
  _prevState: ReadonlyURLSearchParams,
  newParams: URLSearchParams,
): ReadonlyURLSearchParams => newParams as unknown as ReadonlyURLSearchParams;

const useNavigation = (justUpdateUrl?: Props) => {
  const router = useRouter();
  const locale = useLocale();
  const pathname = usePathname();
  const pathNameUrl = getPathname({
    href: pathname,
    locale,
  });

  const realSearchParams = useSearchParams();

  const [isPending, startTransition] = useTransition();

  // من غير generics صريحة على الاستدعاء نفسه، TS هيستنتجها من الـ reducer
  const [optimisticSearchParams, setOptimisticSearchParams] = useOptimistic(
    realSearchParams,
    searchParamsReducer,
  );

  const updateQueryParams = useCallback(
    (updateFn: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(realSearchParams.toString());
      updateFn(params);

      const queryString = params.toString();
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

      startTransition(() => {
        setOptimisticSearchParams(params);

        if (justUpdateUrl?.justUpdateUrl) {
          const localizedUrl = `/${locale}${newUrl.startsWith('/') ? newUrl : `/${newUrl}`}`;
          window.history.replaceState(null, '', localizedUrl);
        } else {
          router.push(newUrl, { scroll: false, locale });
        }
      });
    },
    [realSearchParams, pathname, router, justUpdateUrl, locale, setOptimisticSearchParams],
  );

  const createQueryString = useCallback(
    (queryParams: Record<string, string | string[]>) => {
      updateQueryParams((params) => {
        Object.entries(queryParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            params.delete(key);
            value.forEach((v) => params.append(key, v));
          } else {
            params.set(key, value);
          }
        });
      });
    },
    [updateQueryParams],
  );

  const removeQueryString = useCallback(
    (names: string | string[]) => {
      updateQueryParams((params) => {
        if (Array.isArray(names)) {
          names.forEach((name) => params.delete(name));
        } else {
          params.delete(names);
        }
      });
    },
    [updateQueryParams],
  );

  return {
    createQueryString,
    removeQueryString,
    searchParams: optimisticSearchParams,
    isPending,
  };
};

export default useNavigation;
